import React, { useEffect, useState } from "react";
import { Autocomplete, Dialog, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import ErrorModal from "./ErrorModal";
import SuccessModal from "./SuccessModal";
import {
  getChildAgents,
  getSinglAgentDetails,
  transferStockToDownlineAgent,
} from "../../../../services/stockTransferToDownlineAgentsService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Function to detect if dark mode is active from Tailwind's dark class
const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
    },
    "& .MuiPaper-root": {
      width: "90%",
      maxWidth: "100%",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
      [theme.breakpoints.up("sm")]: {
        width: "90%",
      },
      [theme.breakpoints.up("md")]: {
        width: "600px",
      },
      [theme.breakpoints.up("lg")]: {
        width: "700px",
      },
    },
  };
});

// Custom TextField with dark mode support
const CustomTextField = ({ sx, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <TextField
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        ...sx,
      }}
    />
  );
};

// Custom Autocomplete with dark mode support
const CustomAutocomplete = ({ options, getOptionLabel, value, onChange, renderInput, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={onChange}
      renderInput={renderInput}
      sx={{
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ff6666",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#CD0C02 !important",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": {
            color: "#CD0C02 !important",
          },
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-clearIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
      }}
      {...props}
    />
  );
};

export default function TransferModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filter, setFilter] = useState({ search: "" });
  const [agentDetails, setAgentDetails] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [payload, setPayload] = useState({
    agentId: "",
    amount: null,
    total_amount: null,
    commission_rate: 0,
    commission_amount: 0
  });

  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const {
    data: agents,
    isLoading,
    isError: fetchError,
    error,
  } = useQuery({
    queryKey: ["childAgents", filter],
    queryFn: () => getChildAgents(filter),
  });

  useEffect(() => {
    if (payload.agentId) {
      const selectedAgent = agents?.data?.find(agent => agent.uid === payload.agentId);
      if (selectedAgent) {
        setAgentDetails(selectedAgent);
        
        const commissionPercentage = selectedAgent.commission?.percentage || 
                                    selectedAgent.agentDetail?.commissionRateDetails?.percentage || 
                                    0;
        
        setPayload(prev => ({
          ...prev,
          commission_rate: parseFloat(commissionPercentage)
        }));
      }
    } else {
      setAgentDetails(null);
      setPayload(prev => ({
        ...prev,
        commission_rate: 0,
        commission_amount: 0
      }));
    }
  }, [payload.agentId, agents]);

  const handlePayloadChange = (field, value) => {
    setPayload((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setPayload({
      agentId: "",
      amount: null,
      total_amount: null,
      commission_rate: 0,
      commission_amount: 0
    });
    setAgentDetails(null);
    setValidationErrors({});
    setAmount(null);
  };

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    resetForm();
    handleClose();
  };

  const closeErrorModal = () => setIsError(false);

  const checkValidation = () => {
    const errors = {};
    if (!payload.agentId) {
      errors.agentId = t("transferModal2.errors.agentRequired");
    }
    if (!payload.amount) {
      errors.amount = t("transferModal2.errors.amountRequired");
    } else if (parseFloat(payload.amount) <= 0) {
      errors.amount = t("transferModal.errors.amountPositive");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setAmount("");
    setErrorMessage("");
    resetForm();
    onClose();
  };

  const { mutate, isPending: loading } = useMutation({
    mutationFn: (payload) => transferStockToDownlineAgent(payload),
    onSuccess: () => {
      toast.success(t("transferModal2.successMessage"));
      setIsTransferSuccess(true);

      queryClient.invalidateQueries({
        queryKey: ["stockTransferToDownlineAgents"],
        exact: false
      });
    },
    onError: (error) => {
      const message =
        error?.response?.data?.error || t("transferModal2.errorMessage");
      setIsError(true);
      toast.error(message);
      setErrorMessage(message);
    },
  });

  useEffect(() => {
    if (!agentDetails || !payload.amount || parseFloat(payload.amount) <= 0) {
      handlePayloadChange("total_amount", null);
      handlePayloadChange("commission_amount", 0);
      return;
    }

    const baseAmount = parseFloat(payload.amount);
    const commissionRate = payload.commission_rate || 0;
    const commissionAmount = (baseAmount * commissionRate) / 100;
    const totalAmount = baseAmount + commissionAmount;
    
    handlePayloadChange("commission_amount", parseFloat(commissionAmount.toFixed(2)));
    handlePayloadChange("total_amount", parseFloat(totalAmount.toFixed(2)));
    
  }, [agentDetails, payload.amount, payload.commission_rate]);

  const onTransfer = async () => {
    if (!checkValidation()) {
      return;
    }
    
    const transferPayload = {
      downlineAgentId: payload.agentId,
      amount: parseFloat(payload.amount),
      commissionRate: payload.commission_rate,
      commissionAmount: payload.commission_amount,
      totalAmount: payload.total_amount,
      parentAgentId: JSON.parse(localStorage.getItem('user'))?.uid 
    };
    
    mutate(transferPayload);
  };

  const getAgentDisplayName = (agent) => {
    const commission = agent.commission?.percentage || 
                      agent.agentDetail?.commissionRateDetails?.percentage || 
                      0;
    return `${agent.username} (${agent.mobileNumber}) `;
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <SuccessModal
        open={isTransferSuccess}
        onClose={closeSuccessModal}
        amount={amount}
        payload={payload}
      />
      <ErrorModal
        open={isError}
        onClose={closeErrorModal}
        title={t("transferModal2.errorTitle")}
        error={errorMessage}
        onRetry={onTransfer}
      />
      <div className="px-5 py-3">
        <h3 className={`my-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t("transferModal2.title")}
        </h3>

        <div className="w-full grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-6">
            <CustomAutocomplete
              options={agents?.data || []}
              getOptionLabel={(u) => getAgentDisplayName(u)}
              onChange={(e, newValue) => {
                if (newValue) {
                  delete validationErrors.agentId;
                  handlePayloadChange("agentId", newValue?.uid);
                } else {
                  handlePayloadChange("agentId", "");
                }
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label={t("transferModal2.agentLabel")}
                  error={Boolean(validationErrors.agentId)}
                  helperText={
                    validationErrors.agentId && validationErrors.agentId
                  }
                  required
                />
              )}
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <CustomTextField
              variant="outlined"
              label={t("transferModal2.amountLabel")}
              required
              type="number"
              inputProps={{ min: "0", step: "0.01" }}
              onChange={(e) => {
                const value = e.target.value;
                if (value) delete validationErrors.amount;
                handlePayloadChange("amount", value);
                setAmount(value);
              }}
              error={Boolean(validationErrors.amount)}
              helperText={validationErrors.amount && validationErrors.amount}
            />
          </div>

          {agentDetails && payload.amount && parseFloat(payload.amount) > 0 && (
            <div className="col-span-12 mt-2">
              <div className={`p-4 rounded-lg space-y-3 ${
                isDark ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <h4 className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t("transferModal2.calculationSummary")}
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="font-semibold">{t("transferModal2.agentName")}:</span> {agentDetails.username}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="font-semibold">{t("transferModal2.mobile")}:</span> {agentDetails.mobileNumber}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="font-semibold">{t("transferModal2.accountType")}:</span> {agentDetails.agentDetail?.accountType}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="font-semibold">{t("transferModal2.commissionRate")}:</span> 
                    <span className="text-green-600 font-medium ml-1">
                      {payload.commission_rate}%
                    </span>
                  </div>
                </div>
                <div className={`border-t pt-3 mt-2 ${
                  isDark ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {t("transferModal2.baseAmount")}:
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {parseFloat(payload.amount).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm mb-2">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {t("transferModal2.commissionAmount")} ({payload.commission_rate}%):
                    </span>
                    <span className="font-medium text-green-600">
                      +{payload.commission_amount?.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between font-medium text-base border-t pt-2 mt-2 ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {t("transferModal2.totalAmount")}:
                    </span>
                    <span className="text-red-600 text-lg">
                      {payload.total_amount?.toFixed(2)} {t("common3.currency")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}


          {agentDetails && (!payload.amount || parseFloat(payload.amount) <= 0) && (
            <div className={`col-span-12 p-3 rounded-md ${
              isDark ? 'bg-gray-800' : 'bg-gray-50'
            }`}>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-semibold">{t("transferModal2.agentName")}:</span> {agentDetails.username}
                </div>
                <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-semibold">{t("transferModal2.email")}:</span> {agentDetails.email}
                </div>
                <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-semibold">{t("transferModal2.accountType")}:</span> {agentDetails.agentDetail?.accountType}
                </div>
                <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-semibold">{t("transferModal2.mobile")}:</span> {agentDetails.mobileNumber}
                </div>
                <div className="col-span-2">
                  <span className="font-semibold">{t("transferModal2.commissionRate")}:</span> 
                  <span className="text-green-600 font-medium ml-1">
                    {payload.commission_rate}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="w-full flex items-center justify-center mt-7 gap-3">
          <button
            onClick={() => {
              setErrorMessage("");
              setAmount(null);
              resetForm();
              onClose();
            }}
            className={`py-2 px-6 border rounded-md w-[30%] transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-400 text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t("transferModal2.closeButton")}
          </button>
          <button
            onClick={onTransfer}
            disabled={loading || !payload.agentId || !payload.amount}
            className={`text-white bg-[#CD0C02] hover:bg-[#a80101] w-[70%] py-2 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark ? 'dark:bg-red-700 dark:hover:bg-red-800' : ''
            }`}
          >
            {loading ? t("transferModal2.loading") : t("transferModal2.transferButton")}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}